import React from "react";
import TopBarDisplayTitle from "../home/top_bar_components/TopBarDisplayTitle";
import ScheduleDisplayEmployee from "../requests/employee/schedule/ScheduleDisplayEmployee";
import { formatDate } from "../requests/utilities/helpers";
import {
  API_get_schedule,
  API_drop_shift,
  API_rescind_drop_shift,
  API_get_roles,
  API_get_events_base,
  API_get_holidays,
} from "../../../constants";
import { getRightworkAPI } from "../requests/utilities/requests";
import { postRightworkAPI } from "../requests/utilities/requests";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getBusinessWeekStartDayIndex } from "../reporting/dateRangePresets";
import LoadSpinner from "../../../utilities/LoadSpinner";

function PublishedScheduleDisplay(props) {
  const [storeId, setStoreId] = React.useState(props.homeStore);
  const [data, setData] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);

  const currentDate = new Date();

  // Include adjacent-month cells for any configured business week start.
  const getDateRangeForMonth = (month, year) => {
    const lower = new Date(year, month, 1 - 6);
    const upper = new Date(year, month + 1, 7);
    return {
      lower_bound_date: formatDate(lower),
      upper_bound_date: formatDate(upper),
    };
  };

  const initialRange = getDateRangeForMonth(currentDate.getMonth(), currentDate.getFullYear());

  const getApiParams = (range) => ({
    store_id: props.homeStore,
    lower_bound_date: range.lower_bound_date,
    upper_bound_date: range.upper_bound_date,
    specific_date: formatDate(currentDate),
    date_start: formatDate(currentDate),
    date_end: formatDate(currentDate),
    should_return_all_stores: true,
  });

  const fetch_data = async (api_calls, api_params, showLoading) => {
    if (showLoading) setIsLoading(true);
    let api_set_data_name = Object.keys(api_calls);
    let api_list = Object.values(api_calls);
    try {
      const res = await Promise.all(
        api_list.map((api) => getRightworkAPI(api, api_params))
      );
      setData((prevData) => {
        const newData = { ...prevData };
        res.forEach((res, i) => (newData[api_set_data_name[i]] = res.data));
        return newData;
      });
      // Only the call that turned the spinner on should turn it off, so a
      // concurrent secondary fetch (events/holidays) can't clear it early.
      if (showLoading) setIsLoading(false);
    } catch (error) {
      if (showLoading) setIsLoading(false);
    }
  };

  // Events (get_events) and holidays (get_holidays) expect date_start/date_end
  // as the visible range, unlike the schedule call which uses them for a
  // specific date. So they need their own params object spanning the month.
  const getEventsApiParams = (range) => ({
    store_id: props.homeStore,
    date_start: range.lower_bound_date,
    date_end: range.upper_bound_date,
  });

  const fetch_events_and_holidays = (range) => {
    fetch_data(
      { events: API_get_events_base, holidays: API_get_holidays },
      getEventsApiParams(range),
      false
    );
  };

  const currentRangeRef = React.useRef(initialRange);

  const handleMonthChange = (newMonth, newYear) => {
    const range = getDateRangeForMonth(newMonth, newYear);
    currentRangeRef.current = range;
    fetch_data(
      { schedule: API_get_schedule },
      getApiParams(range),
      false
    );
    fetch_events_and_holidays(range);
  };

  const refetchSchedule = () => {
    fetch_data(
      { schedule: API_get_schedule },
      getApiParams(currentRangeRef.current),
      false
    );
  };

  // ----------- POST APIs ----------- //
  const drop_shift = async (shift_drop_id, shift_pickup_id, drop_note) => {
    // Prepare data to send
    const requestData = {
      store_id: storeId,
      shift_drop_id: shift_drop_id,
      ...(shift_pickup_id && { shift_pickup_id: shift_pickup_id }),
      drop_note: drop_note,
    };

    try {
      toast.loading("Submitting Drop Request...", { autoClose: false });

      const res = await postRightworkAPI(API_drop_shift, requestData);

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Drop Request Submitted!", { autoClose: 3000 });

        refetchSchedule();
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed
    }
  };

  const rescind_drop_shift = async (shift_drop_id) => {
    // Prepare data to send
    const requestData = {
      store_id: storeId,
      shift_drop_id: shift_drop_id,
    };

    try {
      toast.loading("Cancelling Drop Request...", { autoClose: false });

      const res = await postRightworkAPI(API_rescind_drop_shift, requestData);

      if (res && res.status === 200) {
        // Handle success
        toast.dismiss();
        toast.success("Drop Request Cancelled!", { autoClose: 3000 });
        refetchSchedule();
      }
    } catch (error) {
      // Handle network error
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }
      console.error("Error:", error); // Handle error as needed
    }
  };

  const trade_shift = async (shift_drop_id, shift_pickup_id) => {
    // Prepare data to send
    const requestData = {
      store_id: storeId,
      shift_drop_id: shift_drop_id,
      ...(shift_pickup_id && { shift_pickup_id: shift_pickup_id }),
    };

    try {
      toast.loading("Submitting Trade Request...", { autoClose: false });

      const res = await postRightworkAPI(API_drop_shift, requestData);

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Trade Request Submitted!", { autoClose: 3000 });

        refetchSchedule();
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed
    }
  };

  const rescind_trade_shift = async (shift_drop_id) => {
    // Prepare data to send
    const requestData = {
      store_id: storeId,
      shift_drop_id: shift_drop_id,
    };

    try {
      toast.loading("Cancelling Trade Request...", { autoClose: false });

      const res = await postRightworkAPI(API_rescind_drop_shift, requestData);

      if (res && res.status === 200) {
        // Handle success
        toast.dismiss();
        toast.success("Trade Request Cancelled!", { autoClose: 3000 });
        refetchSchedule();
      }
    } catch (error) {
      // Handle network error
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }
      console.error("Error:", error); // Handle error as needed
    }
  };

  const myId = data.schedule?.redux?.payload?.me?.store_team_member?.id;
  const myUserId =
    data.schedule?.redux?.payload?.me?.store_team_member?.user_id;
  const scheduleData = data.schedule?.redux?.payload?.schedule;
  const eventsData = data.events;
  const holidaysData = data.holidays;
  const employeeScheduleSyncHash = data.schedule?.redux?.payload?.me?.store_team_member?.employee_hash


  let notMyUpcomingShifts = [];
  if (scheduleData) {
    notMyUpcomingShifts = scheduleData.filter((shift) => {
      // Include the item if its date is today or in the future
      return (
        (shift.date >= formatDate(currentDate)) &
        (shift.store_team_member?.user?.id !== myUserId)
      );
    });
  }

  React.useEffect(() => {
    currentRangeRef.current = initialRange;
    fetch_data(
      { schedule: API_get_schedule, roleNames: API_get_roles },
      getApiParams(initialRange),
      true
    );
    fetch_events_and_holidays(initialRange);
  }, [props.homeStore]);


  return (
    <>
      {!isLoading ? (
        <div>
          <div>
            <TopBarDisplayTitle
              title="Published Schedule"
              storeOptions={props.storeOptions}
              homeStore={props.homeStore}
              storeNames={props.storeNames}
              change_home_store={props.change_home_store}
            />
          </div>
          <div className="mt-12">
            <ScheduleDisplayEmployee
              businessWeekStartDayIndex={getBusinessWeekStartDayIndex(props.storeNames, props.homeStore)}
              scheduleData={scheduleData}
              eventsData={eventsData}
              holidaysData={holidaysData}
              notMyUpcomingShifts={notMyUpcomingShifts}
              drop_shift={drop_shift}
              trade_shift={trade_shift}
              rescind_drop_shift={rescind_drop_shift}
              rescind_trade_shift={rescind_trade_shift}
              myId={myId}
              myUserId={myUserId}
              roleNames={data.roleNames}
              homeStore={props.homeStore}
              employeeScheduleSyncHash={employeeScheduleSyncHash}
              onMonthChange={handleMonthChange}
            />
          </div>
        </div>
      ) : (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <LoadSpinner />
        </div>
      )}
    </>
  );
}

export default PublishedScheduleDisplay;
